import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { IMAGES } from "@/lib/site";
import { MaskLine } from "@/components/site/Reveal";

const stats = [
  { k: "24/7", v: "Access" },
  { k: "60s", v: "Fresh Blend" },
  { k: "30g", v: "Protein" },
  { k: "0", v: "Sugar Crash" },
];

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yMol = useTransform(scrollYProgress, [0, 1], [0, 160]);
  const rotMol = useTransform(scrollYProgress, [0, 1], [0, 25]);
  const yText = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <section ref={ref} data-testid="hero" className="relative min-h-screen overflow-hidden bg-[#D9F4CD]">
      {/* Decorative molecule */}
      <motion.img
        style={{ y: yMol, rotate: rotMol }}
        src={IMAGES.molecule}
        alt=""
        aria-hidden
        className="pointer-events-none absolute -right-10 top-24 w-[45vw] max-w-[560px] opacity-90 select-none z-0"
      />
      {/* Decorative beads */}
      <img
        src={IMAGES.beads}
        alt=""
        aria-hidden
        className="pointer-events-none absolute -left-10 bottom-40 w-[55vw] max-w-[680px] opacity-25 select-none z-0"
      />
      {/* Sky wash */}
      <div className="pointer-events-none absolute -top-20 -left-20 h-[420px] w-[420px] rounded-full bg-[#C2E9FF] blur-[120px] opacity-60 z-0" />

      <motion.div style={{ y: yText }} className="relative z-10 mx-auto max-w-[1400px] px-5 md:px-10 pt-36 md:pt-48 pb-16">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="font-mono-eb text-xs md:text-sm uppercase tracking-[0.3em] text-[#7B9C62] mb-6 md:mb-10"
        >
          Protein &amp; Wellness {"\u2014"} Vending, Reinvented
        </motion.div>

        <h1 className="font-display uppercase leading-[0.85] text-[15vw] md:text-[13vw] lg:text-[11rem] tracking-tighter text-[#17320B]">
          <MaskLine delay={0.35}>Fuel</MaskLine>
          <MaskLine delay={0.5}>
            <span className="text-[#7B9C62]">On</span> Demand.
          </MaskLine>
        </h1>

        <div className="mt-8 md:mt-12 grid md:grid-cols-2 gap-8 items-end">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.8 }}
            className="max-w-md text-base md:text-lg text-[#17320B]/70 leading-relaxed"
          >
            Freshly blended protein shakes and functional wellness drinks, dispensed in 60 seconds
            from smart machines placed exactly where you train, work and move.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.05, duration: 0.8 }}
            className="flex md:justify-end gap-3"
          >
            <a href="#menu" data-testid="hero-menu-btn" onClick={(e) => { e.preventDefault(); window.lenis ? window.lenis.scrollTo("#menu", { offset: -70, duration: 1.2 }) : document.querySelector("#menu").scrollIntoView({ behavior: "smooth" }); }} className="btn-sweep rounded-full bg-[#17320B] px-7 py-4 font-mono-eb text-xs uppercase tracking-[0.15em] text-[#D9F4CD] font-semibold">
              See the Menu
            </a>
            <a href="#how" data-testid="hero-how-btn" onClick={(e) => { e.preventDefault(); window.lenis ? window.lenis.scrollTo("#how", { offset: -70, duration: 1.2 }) : document.querySelector("#how").scrollIntoView({ behavior: "smooth" }); }} className="rounded-full border border-[#17320B]/25 px-7 py-4 font-mono-eb text-xs uppercase tracking-[0.15em] text-[#17320B] hover:border-[#7B9C62] hover:text-[#7B9C62] transition-colors duration-300">
              How It Works
            </a>
          </motion.div>
        </div>
      </motion.div>

      {/* Stats bar */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.2, duration: 0.8 }}
        className="relative z-10 border-t border-[#17320B]/15 bg-[#D9F4CD]/60 backdrop-blur-md"
      >
        <div className="mx-auto max-w-[1400px] px-5 md:px-10 grid grid-cols-2 md:grid-cols-4 divide-x divide-[#17320B]/15">
          {stats.map((s) => (
            <div key={s.v} className="py-6 md:py-8 px-2 md:px-6 first:pl-0">
              <div className="font-display text-3xl md:text-5xl text-[#17320B]">{s.k}</div>
              <div className="font-mono-eb text-[10px] md:text-xs uppercase tracking-[0.2em] text-[#7B9C62] mt-1">{s.v}</div>
            </div>
          ))}
        </div>
      </motion.div>

      <motion.div
        animate={{ y: [0, 10, 0] }}
        transition={{ repeat: Infinity, duration: 2 }}
        className="pointer-events-none absolute bottom-28 right-6 md:right-10 z-10 text-[#17320B]/40 hidden sm:block"
      >
        <ArrowDown size={22} />
      </motion.div>
    </section>
  );
}
